import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { api_paths } from "../../config/api.paths";
import API from "../../config/axios";
import { SET_LOADING } from "../../redux/user";
import { toastErrorMessage, toastSuccessMessage } from "../../utils/utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PaymentStatusModal from "../common/PaymentStatusModal";
import { PiCheckCircleFill } from "react-icons/pi";
import SecurePaymentLock from "../common/SecurePaymentLock";

function ReserveTicket({
  event,
  onTicketsUpdated,
  purchasedHolderIds = new Set(),
}) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [members, setMembers] = useState([]);
  const [inactiveMembers, setInactiveMembers] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());
  // No longer needed: includeSelf, purchasedTicketHolders, etc. handled by API
  const [hasFetched, setHasFetched] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failed', or 'cancelled'
  const { user } = useSelector((state) => state.USER_DATA);

  const handleProcessToPay = async () => {
    try {
      dispatch(SET_LOADING(true));

      const selectedList = members.filter((m) => selectedMemberIds.has(m.entity_id));

      const primaryIncluded = selectedList.some((m) => m.entity_type === "member");
      const familyMembersArray = selectedList
        .filter((m) => m.entity_type === "family_member")
        .map((m) => m.entity_id);

      const payload = {
        amount: Number(totalAmount),
        event_id: event._id,
        primary_included: primaryIncluded,
        member_id: user?._id,
        family_members: familyMembersArray,
        guests: [], // No guests for logged-in users
      };

      const response = await API.post(
        `${api_paths.member.payment.EVENT_CHECKOUT}?type=event&screen=event-detail`,
        payload
      );

      if (response.data?.payload?.token) {
        const token = response.data.payload.token;
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${process.env.REACT_APP_PAYMENT_GATEWAY_URL}hosted-payments`;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ssl_txn_auth_token";
        input.value = token;

        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      } else if (response.data?.payload?.success) {
        setPaymentStatus({ status: "success", payment: true });
        toastSuccessMessage(
          "RSVP Successful! Your attendance has been confirmed. Event tickets have been sent to your email and are also available in your Dashboard." ||
          response.data?.payload?.message ||
          "Tickets reserved successfully!"
        );
        // tell parent to refresh the single PURCHASED_TICKETS API
        // This will trigger a prop update (purchasedHolderIds) which triggers our sync effect
        if (typeof onTicketsUpdated === "function") await onTicketsUpdated();
        await fetchEligibleMembers();
      }
    } catch (err) {
      console.error("Event Reserve Error:", err);
      toastErrorMessage(err?.response?.data?.message || "Reservation failed");
      dispatch(SET_LOADING(false));
    } finally {
      // We don't call SET_LOADING(false) here if success, because onTicketsUpdated -> purchasedHolderIds update -> sync effect will handle it.
      // But if there's an error or no redirect, we should.
      // Safety check: if no prop change is expected, clear loading.
    }
  };

  // ... render ...

  const handleRedirect = () => {
    setPaymentStatus(null);
    // Remove query params from URL without reloading
    navigate(location.pathname, { replace: true });

    setTimeout(() => {
      const section = document.getElementById("purchased-tickets-section");
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const fetchEligibleMembers = async () => {
    try {
      dispatch(SET_LOADING(true));

      const response = await API.get(
        `${api_paths.member.EVENTS.RSVP_MEMBERS}/${event._id}`
      );
      if (response.status === 200) {
        const result = response?.data?.payload?.result || [];
        const inactive = !!response?.data?.payload?.inactive_members;
        setMembers(result);
        setInactiveMembers(inactive);

        // Default selection: select all
        const initialSelected = new Set(result.map((m) => m.entity_id));
        setSelectedMemberIds(initialSelected);
      }
    } catch (error) {
      console.error("Error fetching RSVP members:", error);
    } finally {
      setHasFetched(true);
      dispatch(SET_LOADING(false));
    }
  };

  useEffect(() => {
    if (event._id) {
      fetchEligibleMembers();
    }
    // eslint-disable-next-line
  }, [event._id]);

  // Toggle member selection
  const toggleMember = (memberId) => {
    const next = new Set(selectedMemberIds);
    if (next.has(memberId)) {
      next.delete(memberId);
    } else {
      next.add(memberId);
    }
    setSelectedMemberIds(next);
  };

  // Selections & Amount Calculation
  const totalAmount = members
    .filter((m) => selectedMemberIds.has(m.entity_id))
    .reduce((sum, m) => sum + (Number(m.price) || 0), 0)
    .toFixed(2);

  const totalTicketCount = selectedMemberIds.size;

  const showRsvpList = members.length > 0;
  const showMembershipRequired = !showRsvpList && inactiveMembers;
  const showRsvpSubmitted = !showRsvpList && !inactiveMembers;

  // Handle closing the modal and cleaning the URL
  const handleCloseModal = () => {
    setPaymentStatus(null);
    navigate(`/events/${id}`, { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    const convergeId = params.get("converge_transaction_id");

    if (status) {
      setPaymentStatus({ status, convergeId });
    }
  }, [location]);

  if (!hasFetched) {
    return <div className="ticket-card" style={{ minHeight: "200px" }}></div>;
  }

  return (
    <>
      <div className="ticket-card">
        {showRsvpList ? (
          <>
            <div className="ticket-card-header mb-4">
              <div className="px-4 pt-4 pb-2">
                <h1 className="ticket-card-title fw-semibold text-main f-20">Purchase Ticket</h1>
                <p className="ticket-card-subtitle mb-0 fs-14">
                  Select family members to generate tickets
                </p>
              </div>
            </div>
            <div className="px-4 pb-3">
              {/* Members list */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {members.length === 0 ? (
                  <div className="ticket-sub-desc fs-14 mt-3">
                    No family members are there for RSVP. You can add them from your Dashboard.
                  </div>
                ) : (
                  members.map((m) => {
                    const fullName =
                      `${m.first_name || ""} ${m.last_name || ""}`.trim() || "Unknown";
                    const isSelected = selectedMemberIds.has(m.entity_id);

                    const borderColor = isSelected ? "#F4C04A" : "#E6E6E6";
                    const backgroundColor = isSelected ? "rgba(237,169,43,0.12)" : "#fff";

                    return (
                      <button
                        key={m.entity_id}
                        type="button"
                        onClick={() => toggleMember(m.entity_id)}
                        style={{
                          width: "100%",
                          justifyContent: "space-between",
                          borderRadius: 8,
                          padding: "10px 12px",
                          border: `1px solid ${borderColor}`,
                          background: backgroundColor,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 8,
                          boxShadow: "none",
                        }}
                      >
                        <span style={{ fontSize: 14, fontWeight: 600 }}>
                          {fullName} ({m.relationship})
                          {m.price > 0 && <span className="ms-1 text-muted"> - ${m.price}</span>}
                          {m.price === 0 && <span className="ms-1 text-success"> (Free)</span>}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Amount summary */}
              <div className="mb-2">
                {/* <div className="amount-row mb-3">
            <span>
              Member Price: {activeFamilyMembers.length + (selfIsActive ? 1 : 0)} × $
              {memberPrice || 0}
            </span>
            <strong>${activeFamilyAmount + (selfIsActive ? memberPrice : 0)}</strong>
          </div> */}

                <div className="border-hr mt-4">
                  {/* <div className="amount-row">Total Tickets: {totalTicketCount}</div> */}
                  <div className="amount-row mt-2 pt-2">
                    <span>Total Tickets</span>
                    <strong>{totalTicketCount}</strong>
                  </div>
                  <div className="total-amount fw-semibold d-flex justify-content-between">
                    <p className="fw-semibold">Total Amount</p>
                    <span>${totalAmount}</span>
                  </div>
                </div>
                {Number(totalAmount) > 0 && (
                  <div className="mt-2">
                    <SecurePaymentLock />
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-orange-md w-100 mb-2"
                  onClick={handleProcessToPay}
                  disabled={
                    totalTicketCount === 0 ||
                    event.is_rsvp_open === false ||
                    event?.member_rsvp_open === false
                  }
                >
                  {event.is_rsvp_open === false || event?.member_rsvp_open === false
                    ? "RSVP Closed"
                    : "RSVP & Purchase Ticket"}
                </button>
              </div>
            </div>
          </>
        ) : showMembershipRequired ? (
          <div className="mb-2 text-center py-4 px-4">
            <div className="mb-3">
              <PiCheckCircleFill className="text-warning" size={60} />
            </div>

            <h2 className="text-warning fw-bold mb-2">Membership Required</h2>
            <p className="text-muted fs-14 mb-4">
              Some members are inactive. Please complete their membership to proceed with RSVP.
            </p>
            <button
              type="button"
              className="btn btn-orange px-4 w-100"
              onClick={() => navigate("/membership")}
            >
              View Membership
            </button>
          </div>
        ) : showRsvpSubmitted ? (
          <div className="mb-2 text-center py-4 px-4">
            <div className="mb-3">
              <PiCheckCircleFill className="text-success" size={60} />
            </div>

            <h2 className="text-success fw-bold mb-2">RSVP Submitted</h2>
            <p className="text-muted fs-14 mb-4">
              Your RSVP has been successfully submitted for all members.
            </p>
            <button type="button" className="btn btn-orange px-4 w-100" onClick={handleRedirect}>
              View Tickets
            </button>
          </div>
        ) : null}
      </div>

      {paymentStatus && (
        <PaymentStatusModal
          status={paymentStatus?.status}
          convergeId={paymentStatus?.convergeId}
          onClose={handleCloseModal}
          onNavigate={handleRedirect}
          buttonName={"event"}
          type={"event"}
          screen={"reserve"}
          payment={paymentStatus.payment}
        />
      )}
    </>
  );
}

export default ReserveTicket;
